import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DataTable, {
  DataTableColumn,
  DataTableRow,
} from "../carbon/DataTable/DataTable";
import EmptyState from "../carbon/EmptyState/EmptyState";
import { TableTitle } from "..";
import {
  addressFormatter,
  TO_BE_AGREED,
} from "../../utils";
import {
  InvitationPaginatedResponse,
  InvitationResponse,
  MaterialType,
} from "../../types";
import { useBanner, useUser } from "../../context";

interface InvitationRow extends DataTableRow {
  id: string;
  material: ReactNode;
  deliveryFormat: ReactNode;
  address: ReactNode;
  /** The material id the row navigates to; null when the invite carries no material. */
  materialId: string | null;
}

type UserMaterialOpportunitiesTableProps = {
  pageNumber: number;
  setPageNumber: (pageNumber: number) => void;
  materialInvitationsOpportunities?: InvitationPaginatedResponse;
};

/**
 * The supplier home's material-invitation table (direct material invites + email invites) — migrated
 * to the shared Carbon `DataTable`. Server-paginated via the page-based invitation hooks (the backend
 * fixes the page size), so the items-per-page dropdown is display-only. A row click opens the
 * material opportunity, unless the seller isn't validated yet, in which case it raises the same
 * "complete your registration" banner the legacy row link did.
 */
const UserMaterialOpportunitiesTable = ({
  pageNumber,
  setPageNumber,
  materialInvitationsOpportunities,
}: UserMaterialOpportunitiesTableProps) => {
  const { isNew, isPending } = useUser();
  const { openBanner, closeBanner } = useBanner();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isNotValidated = isNew || isPending;

  const headers: DataTableColumn<InvitationRow>[] = useMemo(
    () => [
      { key: "material", header: t("opportunities.table.material") },
      { key: "deliveryFormat", header: t("opportunities.table.deliveryFormat") },
      { key: "address", header: t("opportunities.table.address") },
    ],
    [t]
  );

  const rows: InvitationRow[] = useMemo(() => {
    const invites = materialInvitationsOpportunities?.content ?? [];
    return invites.map((invite: InvitationResponse) => {
      const material = invite.material;
      const category = material?.category?.name;
      const isGoods =
        category === MaterialType.DIRECT_GOODS ||
        category === MaterialType.INDIRECT_GOODS;
      return {
        id: invite.id,
        materialId: material?.id ?? null,
        material: (
          <span className="opportunities__material-name">
            {material?.globalMaterialName ?? "-"}
          </span>
        ),
        deliveryFormat: isGoods ? material?.deliveryFormat ?? "-" : "-",
        address: material?.deliveryPlace
          ? addressFormatter(material.deliveryPlace.address)
          : TO_BE_AGREED,
      };
    });
  }, [materialInvitationsOpportunities]);

  const handleRowClick = (row: InvitationRow) => {
    if (isNotValidated) {
      openBanner(t("messages.new"));
      setTimeout(() => closeBanner(), 4000);
      return;
    }
    if (row.materialId) navigate(`/opportunities/material/${row.materialId}`);
  };

  return (
    <div className="seller__invitations">
      <TableTitle title={t("messages.invitations")} />
      <DataTable
        headers={headers}
        rows={rows}
        isLoading={false}
        pagination={{
          page: pageNumber + 1,
          pageSize: materialInvitationsOpportunities?.size ?? rows.length ?? 10,
          totalItems: materialInvitationsOpportunities?.totalElements ?? 0,
          onPageChange: (newPage) => setPageNumber(newPage - 1),
        }}
        onRowClick={handleRowClick}
        empty={<EmptyState title={t("opportunities.noOpportunities")} />}
      />
    </div>
  );
};

export default UserMaterialOpportunitiesTable;
